import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { firstValueFrom } from 'rxjs';

import { KpiCard } from '@core/components/kpi-card/kpi-card';
import { ServiceResponseV1 } from '@core/models/apis';
import { ProducerStatus, ProducerV1 } from '@core/models/producer-admin';
import { GeofenceService } from '@core/services/geofence';
import { ProducersService } from '@core/services/producers';

@Component({
  selector: 'app-dashboard',
  imports: [KpiCard, ButtonModule],
  templateUrl: './dashboard-cards.html',
  styleUrl: './dashboard-cards.scss'
})
export class DashboardCards implements OnInit {
  private readonly geofenceApis = inject(GeofenceService);
  private readonly producersAPIs = inject(ProducersService);
  private readonly router = inject(Router);

  readonly totalProducers = signal<number>(0);
  readonly totalOnboardedProducers = signal<number>(0);
  readonly totalGeofences = computed(() => this.geofenceApis.geofences().length);

  readonly loading = signal<boolean>(true);
  readonly error = signal<boolean>(false);

  readonly hasData = computed(() => this.totalProducers() > 0 || this.totalGeofences() > 0);

  readonly isReady = computed(() => !this.loading() && !this.error());

  ngOnInit() {
    this.loadDashboardData();
  }

  navigateToPath(path: string[]) {
    this.router.navigate(path);
  }

  navigateToActiveProducers() {
    this.router.navigate(['producers'], { queryParams: { status: ProducerStatus.Active } });
  }

  async refresh() {
    await this.loadDashboardData();
  }

  private async loadDashboardData() {
    this.loading.set(true);
    this.error.set(false);

    try {
      const [leadResponse, pendingResponse, readyResponse, activeResponse] = await Promise.all([
        firstValueFrom(this.producersAPIs.getProducersList({ status: ProducerStatus.Lead, pageSize: 1 })),
        firstValueFrom(this.producersAPIs.getProducersList({ status: ProducerStatus.Pending, pageSize: 1 })),
        firstValueFrom(this.producersAPIs.getProducersList({ status: ProducerStatus.Ready, pageSize: 1 })),
        firstValueFrom(this.producersAPIs.getProducersList({ status: ProducerStatus.Active, pageSize: 1 })),
        firstValueFrom(this.geofenceApis.getAll())
      ]);

      this.totalProducers.set(this.countOf(leadResponse) + this.countOf(pendingResponse));
      this.totalOnboardedProducers.set(this.countOf(readyResponse) + this.countOf(activeResponse));

      this.loading.set(false);
      this.error.set(false);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      this.error.set(true);
      this.loading.set(false);
    }
  }

  private countOf(response: ServiceResponseV1<ProducerV1[]>): number {
    return response.paging?.totalSize ?? response.data?.length ?? 0;
  }
}
